import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../apiConfig';

export default function NewsManager() {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ title: '', summary: '', url: '', image: '', published_at: '' });
    const [editing, setEditing] = useState(null);
    const [message, setMessage] = useState('');
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        const fetchNews = async () => {
            setLoading(true);
            try {
                const res = await fetch(getApiUrl('api_news.php'));
                if (!res.ok) throw new Error('Apache server error');
                const data = await res.json();
                setNews(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Failed to fetch news', err);
                setMessage('⚠ Connection failed. Please check if Apache is running in XAMPP.');
                // Fallback to local storage
                const localNews = localStorage.getItem('site_news');
                if (localNews) setNews(JSON.parse(localNews));
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
    }, []);

    const handleSubmit = async (e) => {
        // 1. Immediately prevent default form behavior (page refresh)
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        setMessage('');
        console.log('Publishing news...');

        try {
            const id = editing || Date.now();
            const newItem = { ...form, id };

            // 2. Calculate the complete updated list
            const currentNews = Array.isArray(news) ? news : [];
            const updatedNews = editing
                ? currentNews.map(n => n.id === editing ? newItem : n)
                : [newItem, ...currentNews];

            // 3. Update the UI state first (Fast response)
            setNews(updatedNews);

            // 4. Save to Local Storage immediately as a safe fallback
            try {
                localStorage.setItem('site_news', JSON.stringify(updatedNews));
            } catch (err) {
                console.warn('Local storage backup failed', err);
            }

            // 5. Send to Server (Cross-device sync)
            try {
                const response = await fetch(getApiUrl('api_news.php'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedNews)
                });

                if (response.ok) {
                    setMessage(editing ? '✅ News updated & synced!' : '✅ News published & synced!');
                } else {
                    throw new Error('Sync failed');
                }
            } catch (apiErr) {
                console.error('Server sync failed:', apiErr);
                setMessage('⚠ Saved locally, but failed to sync to server. Check XAMPP Apache.');
            }

            // 6. Reset the form but STAY on this page
            setForm({ title: '', summary: '', url: '', image: '', published_at: '' });
            setEditing(null);
            setImagePreview('');

            // Auto-scroll to top so success message is visible
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Clear status message after a few seconds
            setTimeout(() => setMessage(''), 4000);

        } catch (err) {
            console.error('Critical submit error:', err);
            setMessage('⚠ Error: ' + err.message);
        }
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this news?')) {
            const updatedNews = news.filter(n => n.id !== id);
            setNews(updatedNews);

            // Sync to API
            fetch(getApiUrl('api_news.php'), {
                method: 'POST',
                body: JSON.stringify(updatedNews)
            }).catch(e => console.error('API Save Error:', e));

            localStorage.setItem('site_news', JSON.stringify(updatedNews));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setMessage('⚠ Image size should be less than 2MB');
                return;
            }

            // Check file type
            if (!file.type.startsWith('image/')) {
                setMessage('⚠ Please select a valid image file');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setForm({ ...form, image: base64String });
                setImagePreview(base64String);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setForm({ ...form, image: '' });
        setImagePreview('');
    };

    return (
        <div className="manager-container">
            <div className="manager-header">


            </div>

            <div className="admin-grid">
                <div className="form-section">
                    <h3>{editing ? 'Edit News' : 'Add News'}</h3>
                    {message && (
                        <div style={{
                            padding: '10px',
                            background: '#dcfce7',
                            color: '#15803d',
                            borderRadius: '8px',
                            marginBottom: '15px',
                            textAlign: 'center',
                            fontWeight: '600'
                        }}>
                            {message}
                        </div>
                    )}
                    <div className="publish-form-wrapper">
                        <div className="form-group">
                            <label>NewsTitle</label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="Enter news title..."
                            />
                        </div>
                        <div className="form-group">
                            <label>Summary</label>
                            <textarea
                                value={form.summary}
                                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                                placeholder="Enter a brief summary..."
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', minHeight: '80px' }}
                            />
                        </div>
                        <div className="form-group">
                            <label>News Image</label>
                            <div style={{ marginBottom: '10px' }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                    id="imageUpload"
                                />
                                <label
                                    htmlFor="imageUpload"
                                    style={{
                                        display: 'inline-block',
                                        padding: '10px 20px',
                                        background: '#14a75f',
                                        color: '#fff',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        transition: 'background 0.3s'
                                    }}
                                    onMouseOver={(e) => e.target.style.background = '#0f8a4d'}
                                    onMouseOut={(e) => e.target.style.background = '#14a75f'}
                                >
                                    📷 Choose Image
                                </label>
                                {(imagePreview || form.image) && (
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        style={{
                                            marginLeft: '10px',
                                            padding: '10px 20px',
                                            background: '#dc2626',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        ✕ Remove
                                    </button>
                                )}
                            </div>
                            {(imagePreview || form.image) && (
                                <div style={{ marginTop: '10px' }}>
                                    <img
                                        src={imagePreview || form.image}
                                        alt="Preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '200px',
                                            borderRadius: '8px',
                                            border: '2px solid #ddd'
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="form-group">
                            <label>Published Date</label>
                            <input
                                type="date"
                                value={form.published_at}
                                onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                            />
                        </div>
                        <button
                            type="button"
                            className="login-btn"
                            onClick={(e) => handleSubmit(e)}
                            disabled={!form.title}
                        >
                            {editing ? 'Update News' : 'Publish News'}
                        </button>
                    </div>
                </div>

                <div className="list-section">
                    <h3>Recent News</h3>
                    {loading ? <p>Loading...</p> : (
                        <div className="news-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {news.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>No news articles added yet.</p>
                            ) : (
                                news.map(item => (
                                    <div key={item.id} style={{
                                        padding: '15px',
                                        borderBottom: '1px solid #eee',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '10px'
                                    }}>
                                        <div style={{ display: 'flex', gap: '15px', flex: '1', minWidth: '300px' }}>
                                            {item.image && (
                                                <img src={item.image} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                                            )}
                                            <div>
                                                <h4 style={{ margin: '0 0 5px 0' }}>{item.title}</h4>
                                                <p style={{ margin: '0 0 5px 0', fontSize: '0.9em', color: '#666' }}>{item.summary}</p>
                                                <small style={{ color: '#888' }}>{item.published_at}</small>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                style={{ cursor: 'pointer', padding: '10px 15px' }}
                                                onClick={() => {
                                                    setEditing(item.id);
                                                    setForm(item);
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                className="action-btn edit"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                style={{ cursor: 'pointer', padding: '10px 15px' }}
                                                onClick={() => handleDelete(item.id)}
                                                className="action-btn delete"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
